import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, Eye, RotateCw, X } from "lucide-react";
import { API_URL } from "../config/api";
import { CLOVER_POTIONS } from "../constants/cloverPotions";
import { GEM_FILES, buildGemCatalog, humanizeGemLabel } from "../constants/gems";
import { FAIRY_KEYS } from "../constants/keys";
import { GAME_POTIONS } from "../constants/potions";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import { getAppPortalRoot } from "../utils/appPortal";
import { formatParisDateFR, formatParisTimeFR } from "../utils/parisWallClock";
import { formatQuestNumericDate } from "../utils/questDialogueText";
import { notifyAccountPurged } from "../utils/kingdomMessaging";
import { useAuth } from "./AuthGate";
import OfficialMessagesAdminView from "./OfficialMessagesAdminView";
import PackAssignDropdown from "./PackAssignDropdown";
import CpanelYesNoDropdown from "./CpanelYesNoDropdown";
import VocabInfoTip from "./VocabInfoTip";
import { RadiantTitleCard } from "./RadiantDayTitlesView";
import "./WordEditPanel.css";
import "./DeleteWordModal.css";
import "./RadiantDayTitlesView.css";
import "./ControlPanelView.css";

const STATS_SKELETON_ROWS = 10;

const ADMIN_STAT_METRICS = [
  { value: "capturedWordCount", label: "nombre de mots capturés", hasDetail: true },
  { value: "vocabGameCount", label: "nombre de parties jouées (vocabulaire)" },
  { value: "memoryGameCount", label: "nombre de parties jouées (mémoire)" },
  { value: "lastVocabScore", label: "dernier score obtenu (vocabulaire)", hasDetail: true },
  { value: "lastMemoryScore", label: "dernier score obtenu (mémoire)" },
  { value: "completedQuestCount", label: "nombre de quêtes accomplies" },
  { value: "questHistory", label: "historique de quêtes", hasDetail: true },
  { value: "todayQuest", label: "quête du jour", hasDetail: true },
  { value: "progressionPhase", label: "phase de progression", hasDetail: true },
  { value: "favoriteWordCount", label: "nombre de mots favoris", hasDetail: true },
  { value: "priorityWordCount", label: "nombre de mots prioritaires", hasDetail: true },
  { value: "gemsPotions", label: "potions et pierres", hasDetail: true },
  { value: "relicCount", label: "nombre de reliques", hasDetail: true },
  { value: "titleProgress", label: "avancement des titres", hasDetail: true },
];

const POTION_BY_ID = new Map(
  [...GAME_POTIONS, ...CLOVER_POTIONS].map((potion) => [potion.id, potion])
);

const ADMIN_INVENTORY_KINDS = [
  { value: "gems", label: "modifier les quantités de pierres", noun: "pierres" },
  { value: "potions", label: "modifier les quantités de potions", noun: "potions" },
  { value: "parchments", label: "modifier les quantités de parchemins", noun: "parchemins" },
];

const DEFAULT_ADMIN_INVENTORY_KIND = "gems";

/** Tous les objets d'un type (possédés ou non) pour la grille d'inventaire admin. */
function buildAdminInventoryItems(kind) {
  if (kind === "gems") {
    return buildGemCatalog(GEM_FILES).map((gem) => ({
      id: gem.id,
      label: gem.label,
      image: gem.imageUrl,
    }));
  }
  if (kind === "potions") {
    return [...GAME_POTIONS, ...CLOVER_POTIONS].map((potion) => ({
      id: potion.id,
      label: potion.name,
      image: potion.image,
    }));
  }
  return FAIRY_KEYS.map((key) => ({
    id: key.id,
    label: key.name,
    image: key.image,
  }));
}

function parseInventoryDraft(draft) {
  const text = String(draft ?? "").trim();
  if (!/^\d{1,6}$/.test(text)) return null;
  return Number(text);
}

const OBJECTIVE_STATUS_LABELS = {
  pending: "En cours",
  completed: "Terminé",
  failed: "Échoué",
};

const DEFAULT_ADMIN_STAT_METRIC = "capturedWordCount";

const REQUEST_STATUS_LABELS = {
  pending: "En attente",
  approved: "Invitation envoyée",
  rejected: "Refusée",
  completed: "Compte créé",
};

const ACCOUNT_STATUS_LABELS = {
  CONFIRMED: "Confirmé",
  FORCE_CHANGE_PASSWORD: "Mot de passe temporaire",
  RESET_REQUIRED: "Réinitialisation requise",
  UNCONFIRMED: "Non confirmé",
  ORPHAN: "Données orphelines",
};

function formatWhen(value) {
  if (value == null || value === "") return "—";
  const numeric = Number(value);
  const date =
    Number.isFinite(numeric) && numeric > 0
      ? new Date(numeric < 1e12 ? numeric * 1000 : numeric)
      : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatAdminWhen(value) {
  if (!value) return "—";
  const date = formatParisDateFR(value);
  const time = formatParisTimeFR(value);
  if (!date || !time) return "—";
  return `${date} à ${time}`;
}

function formatAdminScore(score, total) {
  if (score == null || !Number.isFinite(Number(score))) return "—";
  const scoreNum = Number(score);
  if (total != null && Number.isFinite(Number(total)) && Number(total) > 0) {
    return `${scoreNum} / ${Number(total)}`;
  }
  return String(scoreNum);
}

function formatAdminMetricValue(row, metric) {
  if (metric === "lastVocabScore") {
    return formatAdminScore(row.lastVocabScore, row.lastVocabTotal);
  }
  if (metric === "lastMemoryScore") {
    return formatAdminScore(row.lastMemoryScore, row.lastMemoryTotal);
  }
  if (metric === "todayQuest") {
    return row.todayQuestLabel || "—";
  }
  if (metric === "progressionPhase") {
    return row.progressionPhaseLabel || "—";
  }
  if (metric === "questHistory") {
    const count = Number(row.questHistoryCount);
    const total = Number.isFinite(count) ? count : 0;
    return `${total} quêtes`;
  }
  if (metric === "gemsPotions") {
    return `G(${Number(row.gemCount) || 0}), P(${Number(row.potionCount) || 0})`;
  }
  if (metric === "titleProgress") {
    const acquired = Number(row.acquiredTitleCount);
    const total = Number(row.standardTitleCount);
    if (!Number.isFinite(acquired)) return "—";
    return `${acquired}/${Number.isFinite(total) && total > 0 ? total : 12}`;
  }
  const value = Number(row?.[metric]);
  return Number.isFinite(value) ? String(value) : "0";
}

function metricHasDetail(metric) {
  return Boolean(ADMIN_STAT_METRICS.find((option) => option.value === metric)?.hasDetail);
}

function isStatEyeDisabled(row, metric) {
  if (!row?.userId) return true;
  if (metric === "capturedWordCount") {
    return Number(row.capturedWordCount) <= 0;
  }
  if (metric === "questHistory") {
    return Number(row.questHistoryCount) <= 0;
  }
  if (metric === "priorityWordCount") {
    return Number(row.priorityWordCount) <= 0;
  }
  return false;
}

function groupRelicsByCollection(relics) {
  const groups = new Map();
  for (const relic of relics) {
    const collection = String(relic?.collection || "").trim() || "Autres";
    if (!groups.has(collection)) groups.set(collection, []);
    groups.get(collection).push(relic);
  }
  return [...groups.keys()]
    .sort((left, right) => left.localeCompare(right, "fr", { sensitivity: "base" }))
    .map((collection) => ({
      collection,
      relics: [...groups.get(collection)].sort((left, right) =>
        String(left?.name || left?.id || "").localeCompare(
          String(right?.name || right?.id || ""),
          "fr",
          { sensitivity: "base" }
        )
      ),
    }));
}

function QuestsTableColgroup() {
  return (
    <colgroup>
      <col className="cpanel-stats-col cpanel-quests-col--name" />
      <col className="cpanel-stats-col cpanel-quests-col--email" />
      <col className="cpanel-stats-col cpanel-quests-col--choice" />
      <col className="cpanel-stats-col cpanel-quests-col--choice" />
    </colgroup>
  );
}

function StatsTableColgroup() {
  return (
    <colgroup>
      <col className="cpanel-stats-col cpanel-stats-col--name" />
      <col className="cpanel-stats-col cpanel-stats-col--email" />
      <col className="cpanel-stats-col cpanel-stats-col--when" />
      <col className="cpanel-stats-col cpanel-stats-col--when" />
      <col className="cpanel-stats-col cpanel-stats-col--metric" />
    </colgroup>
  );
}

function InventoryTableColgroup() {
  return (
    <colgroup>
      <col className="cpanel-stats-col cpanel-quests-col--name" />
      <col className="cpanel-stats-col cpanel-quests-col--email" />
      <col className="cpanel-stats-col cpanel-inventory-col--actions" />
    </colgroup>
  );
}

function QuestHistoryTableColgroup() {
  return (
    <colgroup>
      <col className="cpanel-quest-history-col cpanel-quest-history-col--date" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--title" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--instructor" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--done" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--validated" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--reward" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--relic" />
      <col className="cpanel-quest-history-col cpanel-quest-history-col--goals" />
    </colgroup>
  );
}

function CpanelSkel({ className = "" }) {
  return <span className={`cpanel-skel ${className}`.trim()} />;
}

function InventoryItemCard({ item, count, busy, onSave }) {
  const [draft, setDraft] = useState(String(count));

  useEffect(() => {
    setDraft(String(count));
  }, [count]);

  const parsed = parseInventoryDraft(draft);
  const changed = draft.trim() !== String(count);
  const canSave = changed && parsed != null && !busy;

  const submit = (event) => {
    event.preventDefault();
    if (!canSave) return;
    onSave(item.id, parsed);
  };

  return (
    <li className={`cpanel-inventory-item${busy ? " cpanel-inventory-item--busy" : ""}`}>
      <div className="cpanel-inventory-item__thumb">
        <img src={item.image} alt="" decoding="sync" draggable={false} />
      </div>
      <p className="cpanel-inventory-item__label" title={item.label}>
        {item.label}
      </p>
      <form className="cpanel-inventory-item__row" onSubmit={submit}>
        <input
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          className={`cpanel-inventory-item__input${
            changed && parsed == null ? " cpanel-inventory-item__input--invalid" : ""
          }`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={busy}
          aria-label={`Quantité de ${item.label}`}
        />
        <span className="cpanel-inventory-item__ok-slot">
          {changed ? (
            <button
              type="submit"
              className="cpanel-inventory-item__ok"
              disabled={!canSave}
              aria-label={`Valider la quantité de ${item.label}`}
            >
              OK
            </button>
          ) : null}
        </span>
      </form>
    </li>
  );
}

function CpanelScrollArea({
  children,
  className = "",
  bodyClassName = "",
  resetKey,
  tableHead = null,
}) {
  const scrollRef = useRef(null);
  const [fades, setFades] = useState({ top: false, bottom: false });
  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(scrollRef);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setFades({ top: false, bottom: false });
      updateScrollbar();
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;
    const hasOverflow = scrollHeight > clientHeight + 1;
    setFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });
    updateScrollbar();
  }, [updateScrollbar]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const frame = requestAnimationFrame(update);
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [update, resetKey]);

  return (
    <div
      className={[
        "cpanel-scroll-shell",
        tableHead ? "cpanel-scroll-shell--table" : "",
        scrollbar.visible ? "cpanel-scroll-shell--scrollable" : "",
        fades.top ? "cpanel-scroll-shell--fade-top" : "",
        fades.bottom ? "cpanel-scroll-shell--fade-bottom" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {tableHead}
      <div className="cpanel-scroll-fade-host">
        <div
          ref={scrollRef}
          className={["cpanel-scroll-body", bodyClassName].filter(Boolean).join(" ")}
          onScroll={update}
        >
          {children}
        </div>
      </div>
      <div
        ref={trackRef}
        className={[
          "cpanel-scroll-bar",
          !scrollbar.visible ? "cpanel-scroll-bar--hidden" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden="true"
        onPointerDown={handleTrackPointerDown}
      >
        {scrollbar.visible ? (
          <div
            className="cpanel-scroll-bar__thumb"
            style={{
              height: `${scrollbar.height}px`,
              transform: `translateY(${scrollbar.top}px)`,
            }}
            onPointerDown={handleThumbPointerDown}
          />
        ) : null}
      </div>
    </div>
  );
}

function gemThumbUrl(gemId) {
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}/gems/${encodeURIComponent(`${gemId}.png`)}`;
}

function RewardThumb({ lot, compact = false }) {
  if (!lot) return null;
  const qty = Number(lot.quantity);
  const quantityLabel = Number.isFinite(qty) && qty > 0 ? `×${qty}` : "×1";
  const quantity = Number(lot.quantity) > 1 ? ` ×${Number(lot.quantity)}` : "";
  if (lot.kind === "gem") {
    return (
      <span className={`cpanel-stats-reward${compact ? " cpanel-stats-reward--compact" : ""}`}>
        <img src={gemThumbUrl(lot.itemId)} alt="" />
        <span>
          {compact
            ? quantityLabel
            : `${lot.label || humanizeGemLabel(`${lot.itemId}.png`) || lot.itemId}${quantity}`}
        </span>
      </span>
    );
  }
  const potion = POTION_BY_ID.get(lot.itemId);
  return (
    <span className={`cpanel-stats-reward${compact ? " cpanel-stats-reward--compact" : ""}`}>
      {potion?.image ? <img src={potion.image} alt="" /> : null}
      <span>
        {compact
          ? quantityLabel
          : `${lot.label || potion?.name || lot.itemId}${quantity}`}
      </span>
    </span>
  );
}

function QuestCompletionDot({ completion }) {
  const kind =
    completion === "completed" ? "yes" : completion === "in_progress" ? "progress" : "no";
  const label = kind === "yes" ? "Oui" : kind === "progress" ? "En cours" : "Non";
  return (
    <span
      className={`cpanel-quest-dot cpanel-quest-dot--${kind}`}
      title={label}
      aria-label={label}
    />
  );
}

function QuestHistoryTableHead() {
  return (
    <div className="cpanel-stats-table-head">
      <table className="cpanel-stats-table cpanel-quest-history-table">
        <QuestHistoryTableColgroup />
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Quête</th>
            <th scope="col">Instructeur</th>
            <th scope="col">Accomplie</th>
            <th scope="col">Validation</th>
            <th scope="col">Récompense</th>
            <th scope="col">Relique</th>
            <th scope="col">Objectifs</th>
          </tr>
        </thead>
      </table>
    </div>
  );
}

function QuestHistoryRows({ quests, onOpenObjectives }) {
  if (!quests.length) {
    return (
      <tr>
        <td colSpan={8}>
          <p className="cpanel-view__empty">Aucune quête.</p>
        </td>
      </tr>
    );
  }
  return quests.map((quest, index) => (
    <tr key={`${quest.date || "quest"}-${index}`}>
      <td>{formatQuestNumericDate(quest.date) || "—"}</td>
      <td>{quest.questTitle || "—"}</td>
      <td>{quest.instructorName || "—"}</td>
      <td className="cpanel-quest-history-table__done">
        <QuestCompletionDot completion={quest.completion} />
      </td>
      <td>{quest.completedAt || "—"}</td>
      <td>
        {(quest.rewards || []).length === 0 ? (
          "—"
        ) : (
          <div className="cpanel-quest-history-rewards">
            {(quest.rewards || []).map((lot, rewardIndex) => (
              <RewardThumb
                key={`${lot.kind}-${lot.itemId}-${rewardIndex}`}
                lot={lot}
                compact
              />
            ))}
          </div>
        )}
      </td>
      <td>{quest.relicName || "—"}</td>
      <td>
        <div className="cpanel-quest-history-table__goals">
          <span>
            {Number(quest.completedObjectives) || 0}/{Number(quest.totalObjectives) || 4}
          </span>
          <button
            type="button"
            className="cpanel-stats-eye cpanel-stats-eye--tiny"
            onClick={() => onOpenObjectives?.(quest)}
            aria-label="Voir le détail des objectifs"
          >
            <Eye size={14} aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  ));
}

function QuestObjectivesDetailBody({ quest }) {
  const objectives = Array.isArray(quest?.objectives) ? quest.objectives : [];
  if (objectives.length === 0) {
    return <p className="cpanel-view__empty">Aucun objectif.</p>;
  }
  return (
    <div className="cpanel-stats-detail">
      <p className="cpanel-stats-detail__meta">
        {[
          quest.instructorName,
          quest.questTitle,
          formatQuestNumericDate(quest.date),
        ]
          .filter(Boolean)
          .join(" — ") || "Objectifs"}
      </p>
      <ul className="cpanel-stats-objectives">
        {objectives.map((objective, index) => (
          <li key={objective.id || `${objective.label}-${index}`}>
            <span>{objective.label || `Objectif ${index + 1}`}</span>
            <span
              className={`cpanel-stats-objectives__score cpanel-stats-objectives__score--${
                objective.status || "pending"
              }`}
            >
              {Number(objective.current) || 0}/{Number(objective.target) || 1}
              {" · "}
              {OBJECTIVE_STATUS_LABELS[objective.status] || objective.status || "—"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CapturedWordDetailBody({ data }) {
  if (!data?.found) {
    return <p className="cpanel-view__empty">Mot introuvable.</p>;
  }
  const hiddenWords =
    Array.isArray(data.motsCaches) && data.motsCaches.length > 0
      ? data.motsCaches.join(", ")
      : "—";
  const ewss =
    data.ewssScore == null || !Number.isFinite(Number(data.ewssScore))
      ? "—"
      : String(data.ewssScore);
  return (
    <dl className="cpanel-word-detail">
      <div>
        <dt>Définition</dt>
        <dd>{data.definition || "—"}</dd>
      </div>
      <div>
        <dt>Extrait</dt>
        <dd>{data.extrait || "—"}</dd>
      </div>
      <div>
        <dt>Date de capture</dt>
        <dd>{data.capturedAt || "—"}</dd>
      </div>
      <div>
        <dt>Auteur</dt>
        <dd>{data.auteur || "—"}</dd>
      </div>
      <div>
        <dt>Ouvrage</dt>
        <dd>{data.ouvrage || "—"}</dd>
      </div>
      <div>
        <dt>Mots cachés</dt>
        <dd>{hiddenWords}</dd>
      </div>
      <div>
        <dt>Jeux</dt>
        <dd>{Number(data.gameCount) || 0}</dd>
      </div>
      <div>
        <dt>Réussites</dt>
        <dd>{Number(data.correctCount) || 0}</dd>
      </div>
      <div>
        <dt>Échecs</dt>
        <dd>{Number(data.failCount) || 0}</dd>
      </div>
      <div>
        <dt>Score EWSS</dt>
        <dd>{ewss}</dd>
      </div>
    </dl>
  );
}

function StatDetailBody({ metric, data, onOpenCapturedWord }) {
  if (metric === "lastVocabScore") {
    if (!data?.found) {
      return <p className="cpanel-view__empty">Aucune partie de vocabulaire.</p>;
    }
    const when = data.playedAt
      ? formatAdminWhen(data.playedAt)
      : [data.date, data.time].filter(Boolean).join(" à ") || "—";
    return (
      <div className="cpanel-stats-detail">
        <p className="cpanel-stats-detail__meta">
          Partie du {when}
          {data.score != null
            ? ` · ${formatAdminScore(data.score, data.totalQuestions)}`
            : ""}
        </p>
        {(data.answers || []).length === 0 ? (
          <p className="cpanel-view__empty">Aucun mot enregistré pour cette partie.</p>
        ) : (
          <ol className="cpanel-stats-answers">
            {(data.answers || []).map((answer, index) => (
              <li key={`${answer.mot}-${index}`}>
                <span className="cpanel-stats-answers__mot">
                  {index + 1}. {answer.mot}
                </span>
                <span className="cpanel-stats-answers__given">
                  {answer.given || "—"}
                </span>
                <span
                  className={
                    answer.correct === true
                      ? "cpanel-stats-answers__ok"
                      : answer.correct === false
                        ? "cpanel-stats-answers__ko"
                        : "cpanel-stats-answers__na"
                  }
                >
                  {answer.correct === true
                    ? "Juste"
                    : answer.correct === false
                      ? "Faux"
                      : "—"}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  if (metric === "progressionPhase") {
    return (
      <div className="cpanel-stats-detail">
        <dl className="cpanel-word-detail">
          <div>
            <dt>Phase mémorisée</dt>
            <dd>{data?.storedPhaseLabel || "Pas encore figée"}</dd>
          </div>
          <div>
            <dt>Changement</dt>
            <dd>{data?.storedChangedAt || "—"}</dd>
          </div>
          <div>
            <dt>Quête du jour</dt>
            <dd>{data?.todayQuestPhaseLabel || "Pas encore tirée"}</dd>
          </div>
          <div>
            <dt>Phase recalculée</dt>
            <dd>{data?.computedPhaseLabel || "—"}</dd>
          </div>
          <div>
            <dt>Mots rencontrés</dt>
            <dd>{Number(data?.encounteredWordCount) || 0}</dd>
          </div>
          <div>
            <dt>Mots évaluables</dt>
            <dd>{Number(data?.evaluableWordCount) || 0}</dd>
          </div>
          <div>
            <dt>Mots maîtrisés</dt>
            <dd>{Number(data?.masteredWordCount) || 0}</dd>
          </div>
          <div>
            <dt>Prochain palier</dt>
            <dd>{data?.nextPhaseHint || "—"}</dd>
          </div>
        </dl>
        {data?.ratchetHolds ? (
          <p className="cpanel-stats-detail__hint">
            Le cliquet retient une phase plus haute que le recalcul : le joueur ne
            redescend pas.
          </p>
        ) : null}
      </div>
    );
  }

  if (metric === "todayQuest") {
    if (!data?.found) {
      return <p className="cpanel-view__empty">Aucune quête du jour pour ce joueur.</p>;
    }
    return (
      <div className="cpanel-stats-detail">
        <p className="cpanel-stats-detail__meta">
          {[data.instructorName, data.questTitle].filter(Boolean).join(" — ") || "Quête du jour"}
        </p>
        <p>Statut : {data.status || "—"}</p>
        <ul className="cpanel-stats-objectives">
          {(data.objectives || []).map((objective) => (
            <li key={objective.id || objective.label}>
              <span>{objective.label || objective.id || "Objectif"}</span>
              <span>
                {Number(objective.current) || 0}/{Number(objective.target) || 1}
                {" · "}
                {OBJECTIVE_STATUS_LABELS[objective.status] || objective.status || "—"}
              </span>
            </li>
          ))}
        </ul>
        <div className="cpanel-stats-rewards">
          <p>Récompense proposée</p>
          {(data.rewards || []).length === 0 ? (
            <p className="cpanel-stats-detail__hint">Pas encore tirée.</p>
          ) : (
            <div className="cpanel-stats-rewards__row">
              {(data.rewards || []).map((lot, index) => (
                <RewardThumb key={`${lot.kind}-${lot.itemId}-${index}`} lot={lot} />
              ))}
            </div>
          )}
        </div>
        {data.status === "Terminée" ? (
          <>
            <p>Validation : {data.completedAt || "—"}</p>
            <p>
              Relique découverte :{" "}
              {data.relicDiscovered ? data.relicName || "oui" : "non"}
            </p>
          </>
        ) : null}
      </div>
    );
  }

  if (metric === "capturedWordCount") {
    const words = Array.isArray(data?.words) ? data.words : [];
    if (words.length === 0) {
      return <p className="cpanel-view__empty">Aucun mot capturé.</p>;
    }
    return (
      <ul className="cpanel-stats-words cpanel-stats-words--with-eye">
        {words.map((word) => (
          <li key={word}>
            <span className="cpanel-stats-words__label">{word}</span>
            <button
              type="button"
              className="cpanel-stats-eye"
              onClick={() => onOpenCapturedWord?.(word)}
              aria-label={`Voir le détail de ${word}`}
            >
              <Eye size={17} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    );
  }

  if (metric === "favoriteWordCount" || metric === "priorityWordCount") {
    const words = Array.isArray(data?.words) ? data.words : [];
    if (words.length === 0) {
      return (
        <p className="cpanel-view__empty">
          {metric === "favoriteWordCount"
            ? "Aucun mot favori."
            : "Aucun mot prioritaire."}
        </p>
      );
    }
    return (
      <ul className="cpanel-stats-words">
        {words.map((word) => (
          <li key={word}>{word}</li>
        ))}
      </ul>
    );
  }

  if (metric === "gemsPotions") {
    const gems = Array.isArray(data?.gems) ? data.gems : [];
    const potions = Array.isArray(data?.potions) ? data.potions : [];
    return (
      <div className="cpanel-stats-inventory">
        <section>
          <h3>Pierres</h3>
          {gems.length === 0 ? (
            <p className="cpanel-stats-detail__hint">Aucune pierre.</p>
          ) : (
            <ul>
              {gems.map((gem) => (
                <li key={gem.id}>
                  <img src={gemThumbUrl(gem.id)} alt="" />
                  <span>
                    {humanizeGemLabel(`${gem.id}.png`) || gem.id} ×{gem.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3>Potions</h3>
          {potions.length === 0 ? (
            <p className="cpanel-stats-detail__hint">Aucune potion.</p>
          ) : (
            <ul>
              {potions.map((potion) => {
                const meta = POTION_BY_ID.get(potion.id);
                return (
                  <li key={potion.id}>
                    {meta?.image ? <img src={meta.image} alt="" /> : null}
                    <span>
                      {meta?.name || potion.id} ×{potion.count}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    );
  }

  if (metric === "titleProgress") {
    const titles = Array.isArray(data?.titles) ? data.titles : [];
    if (titles.length === 0) {
      return <p className="cpanel-view__empty">Aucun titre à afficher.</p>;
    }
    return (
      <div className="cpanel-stats-titles">
        {titles.map((title) => (
          <RadiantTitleCard
            key={title.id}
            title={title}
            ultimate={title.id === "jour-radieux"}
          />
        ))}
      </div>
    );
  }

  if (metric === "relicCount") {
    const relics = Array.isArray(data?.relics) ? data.relics : [];
    if (relics.length === 0) {
      return <p className="cpanel-view__empty">Aucune relique.</p>;
    }
    return (
      <div className="cpanel-stats-relic-groups">
        {groupRelicsByCollection(relics).map((group) => (
          <section key={group.collection} className="cpanel-stats-relic-group">
            <h3>{group.collection}</h3>
            <ul className="cpanel-stats-relics">
              {group.relics.map((relic) => (
                <li key={relic.id}>
                  <span>{relic.name || relic.id}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return <p className="cpanel-view__empty">Aucun détail à afficher.</p>;
}

function isOwnAccount(account, user) {
  if (account?.userId && user?.sub && account.userId === user.sub) return true;
  const email = String(account?.email || "").trim().toLowerCase();
  const mine = String(user?.email || "").trim().toLowerCase();
  return Boolean(email && mine && email === mine);
}

function confirmToken(account) {
  return String(account?.email || account?.userId || "").trim();
}

async function adminJson(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    ...options,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || "Impossible de traiter cette action.");
  }
  return json;
}

export default function ControlPanelView({ screen = "requests" }) {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [query, setQuery] = useState("");
  const [stats, setStats] = useState([]);
  const [questPlayers, setQuestPlayers] = useState([]);
  const [statMetric, setStatMetric] = useState(DEFAULT_ADMIN_STAT_METRIC);
  const [statsDetail, setStatsDetail] = useState(null);
  const [inventoryPlayers, setInventoryPlayers] = useState([]);
  const [inventoryKind, setInventoryKind] = useState(DEFAULT_ADMIN_INVENTORY_KIND);
  const [inventoryDetail, setInventoryDetail] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [mfaAccount, setMfaAccount] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState("");

  const load = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      if (screen === "accounts") {
        const json = await adminJson("/admin/users");
        setAccounts(Array.isArray(json.users) ? json.users : []);
      } else if (screen === "stats") {
        const json = await adminJson("/admin/user-stats");
        setStats(Array.isArray(json.users) ? json.users : []);
      } else if (screen === "quests") {
        const json = await adminJson("/admin/quest-instructors");
        setQuestPlayers(Array.isArray(json.users) ? json.users : []);
      } else if (screen === "inventory") {
        const json = await adminJson("/admin/inventories");
        setInventoryPlayers(Array.isArray(json.users) ? json.users : []);
      } else if (screen === "official") {
        return;
      } else {
        const json = await adminJson("/admin/access-requests");
        setRequests(Array.isArray(json.requests) ? json.requests : []);
      }
    } catch (err) {
      setError(err.message || "Chargement impossible.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    setQuery("");
    setInfo("");
    setError("");
    setStatsDetail(null);
    setInventoryDetail(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const filteredRequests = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("fr-FR");
    if (!needle) return requests;
    return requests.filter((item) =>
      String(item.email || "").toLocaleLowerCase("fr-FR").includes(needle)
    );
  }, [requests, query]);

  const filteredAccounts = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("fr-FR");
    if (!needle) return accounts;
    return accounts.filter((item) => {
      const hay = `${item.email || ""} ${item.displayName || ""} ${item.userId || ""}`;
      return hay.toLocaleLowerCase("fr-FR").includes(needle);
    });
  }, [accounts, query]);

  const selectedStatMetric =
    ADMIN_STAT_METRICS.find((option) => option.value === statMetric) ||
    ADMIN_STAT_METRICS[0];

  const openStatDetail = async (row) => {
    if (!row?.userId || !metricHasDetail(selectedStatMetric.value)) return;
    const metric = selectedStatMetric.value;
    setError("");
    setStatsDetail({
      userId: row.userId,
      metric,
      displayName: row.displayName || "",
      email: row.email || "",
      loading: true,
      error: "",
      data: null,
    });
    try {
      const data = await adminJson(
        `/admin/user-stats/${encodeURIComponent(row.userId)}/detail?metric=${encodeURIComponent(metric)}`
      );
      setStatsDetail((current) =>
        current && current.userId === row.userId && current.metric === metric
          ? { ...current, loading: false, data }
          : current
      );
    } catch (err) {
      setStatsDetail((current) =>
        current && current.userId === row.userId && current.metric === metric
          ? {
              ...current,
              loading: false,
              error: err.message || "Chargement impossible.",
            }
          : current
      );
    }
  };

  const openCapturedWord = async (mot) => {
    const detail = statsDetail;
    const word = String(mot || "").trim();
    if (!detail?.userId || !word) return;
    setError("");
    setStatsDetail((current) =>
      current && current.userId === detail.userId
        ? {
            ...current,
            wordDetail: { mot: word, loading: true, error: "", data: null },
          }
        : current
    );
    try {
      const data = await adminJson(
        `/admin/user-stats/${encodeURIComponent(detail.userId)}/detail?metric=${encodeURIComponent(
          "capturedWordCount"
        )}&mot=${encodeURIComponent(word)}`
      );
      setStatsDetail((current) =>
        current && current.userId === detail.userId && current.wordDetail?.mot === word
          ? {
              ...current,
              wordDetail: { mot: word, loading: false, error: "", data },
            }
          : current
      );
    } catch (err) {
      setStatsDetail((current) =>
        current && current.userId === detail.userId && current.wordDetail?.mot === word
          ? {
              ...current,
              wordDetail: {
                mot: word,
                loading: false,
                error: err.message || "Chargement impossible.",
                data: null,
              },
            }
          : current
      );
    }
  };

  const closeStatsDetail = () => {
    setStatsDetail((current) => {
      if (current?.wordDetail) {
        return { ...current, wordDetail: null };
      }
      if (current?.questObjectives) {
        return { ...current, questObjectives: null };
      }
      return null;
    });
  };

  const selectedInventoryKind =
    ADMIN_INVENTORY_KINDS.find((option) => option.value === inventoryKind) ||
    ADMIN_INVENTORY_KINDS[0];

  const inventoryDetailKind = inventoryDetail?.kind || "";
  const inventoryItems = useMemo(
    () => (inventoryDetailKind ? buildAdminInventoryItems(inventoryDetailKind) : []),
    [inventoryDetailKind]
  );

  const openInventoryDetail = async (row) => {
    if (!row?.userId) return;
    const kind = selectedInventoryKind.value;
    setError("");
    setInfo("");
    setInventoryDetail({
      userId: row.userId,
      kind,
      displayName: row.displayName || "",
      email: row.email || "",
      loading: true,
      error: "",
      inventory: null,
      busyItemId: "",
    });
    try {
      const data = await adminJson(`/admin/inventories/${encodeURIComponent(row.userId)}`);
      setInventoryDetail((current) =>
        current && current.userId === row.userId && current.kind === kind
          ? { ...current, loading: false, inventory: data }
          : current
      );
    } catch (err) {
      setInventoryDetail((current) =>
        current && current.userId === row.userId && current.kind === kind
          ? { ...current, loading: false, error: err.message || "Chargement impossible." }
          : current
      );
    }
  };

  const saveInventoryCount = async (itemId, count) => {
    const detail = inventoryDetail;
    if (!detail?.userId || detail.loading || detail.busyItemId) return;
    const { userId, kind } = detail;
    setError("");
    setInfo("");
    setInventoryDetail((current) =>
      current && current.userId === userId ? { ...current, busyItemId: itemId, error: "" } : current
    );
    try {
      const result = await adminJson(`/admin/inventories/${encodeURIComponent(userId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, itemId, count }),
      });
      setInventoryDetail((current) =>
        current && current.userId === userId
          ? {
              ...current,
              busyItemId: "",
              inventory: {
                gems: result.gems || {},
                potions: result.potions || {},
                parchments: result.parchments || {},
              },
            }
          : current
      );
    } catch (err) {
      setInventoryDetail((current) =>
        current && current.userId === userId
          ? { ...current, busyItemId: "", error: err.message || "Mise à jour impossible." }
          : current
      );
    }
  };

  const pendingCount = requests.filter((item) => item.status === "pending").length;

  const runAction = async (key, action, successMessage) => {
    setBusyKey(key);
    setError("");
    setInfo("");
    try {
      await action();
      if (successMessage) setInfo(successMessage);
      await load(true);
    } catch (err) {
      setError(err.message || "Action impossible.");
    } finally {
      setBusyKey("");
    }
  };

  const setPlayerSpecialInstructors = async (row, enabled) => {
    if (!row?.userId || Boolean(row.specialInstructorsEnabled) === enabled) return;
    const previous = Boolean(row.specialInstructorsEnabled);
    setQuestPlayers((current) =>
      current.map((item) =>
        item.userId === row.userId ? { ...item, specialInstructorsEnabled: enabled } : item
      )
    );
    setBusyKey(`quest:${row.userId}`);
    setError("");
    setInfo("");
    try {
      await adminJson(`/admin/quest-instructors/${encodeURIComponent(row.userId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ specialInstructorsEnabled: enabled }),
      });
    } catch (err) {
      setQuestPlayers((current) =>
        current.map((item) =>
          item.userId === row.userId
            ? { ...item, specialInstructorsEnabled: previous }
            : item
        )
      );
      setError(err.message || "Mise à jour impossible.");
    } finally {
      setBusyKey("");
    }
  };

  const setPlayerQuestEmail = async (row, enabled) => {
    if (!row?.userId || Boolean(row.questEmailEnabled) === enabled) return;
    const previous = row.questEmailEnabled !== false;
    setQuestPlayers((current) =>
      current.map((item) =>
        item.userId === row.userId ? { ...item, questEmailEnabled: enabled } : item
      )
    );
    setBusyKey(`quest:${row.userId}`);
    setError("");
    setInfo("");
    try {
      await adminJson(`/admin/quest-email/${encodeURIComponent(row.userId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
    } catch (err) {
      setQuestPlayers((current) =>
        current.map((item) =>
          item.userId === row.userId ? { ...item, questEmailEnabled: previous } : item
        )
      );
      setError(err.message || "Mise à jour impossible.");
    } finally {
      setBusyKey("");
    }
  };

  const decideRequest = (email, action) =>
    runAction(
      `${action}:${email}`,
      () =>
        adminJson("/admin/access-requests/decide", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, action }),
        }),
      action === "approve"
        ? "Un e-mail d'invitation a été envoyé."
        : "La demande a été refusée."
    );

  const removeRequest = (email) =>
    runAction(
      `remove:${email}`,
      () =>
        adminJson(`/admin/access-requests/${encodeURIComponent(email)}`, {
          method: "DELETE",
        }),
      "La demande a été retirée de la liste."
    );

  const expectedConfirm = confirmToken(pendingDelete);
  const confirmMatches =
    expectedConfirm &&
    typedConfirm.trim().toLocaleLowerCase("fr-FR") ===
      expectedConfirm.toLocaleLowerCase("fr-FR");

  const closeDelete = () => {
    if (deleting) return;
    setPendingDelete(null);
    setTypedConfirm("");
    setDeleteError("");
  };

  const confirmDeleteAccount = async () => {
    if (!pendingDelete || !confirmMatches || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const purgedUserId = pendingDelete.userId;
      await adminJson(`/admin/users/${encodeURIComponent(purgedUserId)}`, {
        method: "DELETE",
      });
      notifyAccountPurged(purgedUserId);
      setPendingDelete(null);
      setTypedConfirm("");
      setInfo("Le compte et ses données ont été supprimés.");
      await load(true);
    } catch (err) {
      setDeleteError(err.message || "Suppression impossible.");
    } finally {
      setDeleting(false);
    }
  };

  const closeMfa = () => {
    if (mfaBusy) return;
    setMfaAccount(null);
    setMfaEnabled(null);
    setMfaError("");
  };

  const openMfaAccount = async (account) => {
    setMfaAccount(account);
    setMfaEnabled(null);
    setMfaError("");
    setMfaLoading(true);
    try {
      const json = await adminJson(`/admin/users/${encodeURIComponent(account.userId)}/mfa`);
      setMfaEnabled(json.enabled === true);
    } catch (err) {
      setMfaError(err.message || "Lecture impossible.");
    } finally {
      setMfaLoading(false);
    }
  };

  const disableMfaAccount = async () => {
    if (!mfaAccount?.userId || mfaBusy) return;
    setMfaBusy(true);
    setMfaError("");
    try {
      const json = await adminJson(
        `/admin/users/${encodeURIComponent(mfaAccount.userId)}/mfa/disable`,
        { method: "POST" }
      );
      setMfaEnabled(json.enabled === true);
      setInfo(
        `Authentification renforcée désactivée pour ${
          mfaAccount.email || mfaAccount.displayName || "ce compte"
        }.`
      );
    } catch (err) {
      setMfaError(err.message || "Désactivation impossible.");
    } finally {
      setMfaBusy(false);
    }
  };

  if (screen === "official") {
    return <OfficialMessagesAdminView />;
  }

  return (
    <div
      className={`cpanel-view${
        screen === "stats" || screen === "quests" || screen === "inventory"
          ? " cpanel-view--stats"
          : ""
      }`}
    >
      <header className="cpanel-view__header">
        <h2 className="cpanel-view__title">
          {screen === "accounts"
            ? "Comptes"
            : screen === "stats"
              ? "Utilisateurs de Vocabularium"
              : screen === "quests"
                ? "Quêtes"
                : screen === "inventory"
                  ? "Inventaires"
                  : "Demandes d'accès"}
        </h2>
        {screen === "inventory" ? (
          inventoryDetail ? (
            <div className="cpanel-stats-back-row">
              <button
                type="button"
                className="cpanel-stats-back"
                onClick={() => setInventoryDetail(null)}
                aria-label="Revenir à l'écran d'origine"
              >
                <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <p className="cpanel-stats-detail__who">
                {[
                  [inventoryDetail.displayName, inventoryDetail.email]
                    .filter(Boolean)
                    .join(" · ") || "Joueur",
                  ADMIN_INVENTORY_KINDS.find((option) => option.value === inventoryDetail.kind)
                    ?.noun,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          ) : null
        ) : screen === "stats" ? (
          statsDetail ? (
            <div className="cpanel-stats-back-row">
              <button
                type="button"
                className="cpanel-stats-back"
                onClick={closeStatsDetail}
                aria-label={
                  statsDetail.wordDetail
                    ? "Revenir à la liste des mots"
                    : statsDetail.questObjectives
                      ? "Revenir à l'historique de quêtes"
                      : "Revenir à l'écran d'origine"
                }
              >
                <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <p className="cpanel-stats-detail__who">
                {[
                  statsDetail.displayName,
                  statsDetail.email,
                  statsDetail.wordDetail?.mot,
                  statsDetail.questObjectives?.questTitle,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Joueur"}
              </p>
            </div>
          ) : null
        ) : screen === "quests" ? null : (
          <p className="cpanel-view__intro">
            {screen === "accounts"
              ? "Supprimer un compte retire Cognito, le profil Dynamo et les quêtes de ce joueur. Le catalogue partagé n'est pas touché."
              : pendingCount
                ? `${pendingCount} demande${pendingCount > 1 ? "s" : ""} en attente.`
                : "Aucune demande en attente."}
          </p>
        )}
      </header>

      {screen === "stats" && !statsDetail ? (
        <div className="cpanel-view__toolbar cpanel-view__toolbar--stats">
          <div className="cpanel-stats-metric">
            <PackAssignDropdown
              options={ADMIN_STAT_METRICS}
              value={selectedStatMetric.value}
              onChange={setStatMetric}
              ariaLabel="Indicateur affiché dans le tableau"
              matchTriggerWidth
              openBelow
            />
          </div>
          <button
            type="button"
            className="cpanel-stats-refresh"
            onClick={() => load()}
            disabled={loading}
            aria-label="Actualiser les sommes"
          >
            <RotateCw size={15} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
      ) : screen === "quests" ? (
        <div className="cpanel-view__toolbar cpanel-view__toolbar--stats">
          <button
            type="button"
            className="cpanel-stats-refresh"
            onClick={() => load()}
            disabled={loading}
            aria-label="Actualiser les quêtes"
          >
            <RotateCw size={15} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
      ) : screen === "inventory" && !inventoryDetail ? (
        <div className="cpanel-view__toolbar cpanel-view__toolbar--stats">
          <div className="cpanel-stats-metric">
            <PackAssignDropdown
              options={ADMIN_INVENTORY_KINDS}
              value={selectedInventoryKind.value}
              onChange={setInventoryKind}
              ariaLabel="Type d'objets à modifier"
              matchTriggerWidth
              openBelow
            />
          </div>
          <button
            type="button"
            className="cpanel-stats-refresh"
            onClick={() => load()}
            disabled={loading}
            aria-label="Actualiser les inventaires"
          >
            <RotateCw size={15} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
      ) : screen !== "stats" && screen !== "inventory" ? (
        <div className="cpanel-view__toolbar">
          <input
            type="search"
            className="cpanel-view__search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={
              screen === "accounts"
                ? "Filtrer par e-mail, nom ou identifiant…"
                : "Filtrer par e-mail…"
            }
            aria-label="Filtrer"
          />
          <button
            type="button"
            className="word-edit-btn word-edit-btn--primary cpanel-view__refresh"
            onClick={() => load()}
            disabled={loading || Boolean(busyKey)}
          >
            Actualiser
          </button>
        </div>
      ) : null}

      {error ? <p className="cpanel-view__banner cpanel-view__banner--error">{error}</p> : null}
      {info ? <p className="cpanel-view__banner cpanel-view__banner--info">{info}</p> : null}

      {loading && screen !== "stats" && screen !== "quests" && screen !== "inventory" ? (
        <p className="cpanel-view__empty">Chargement…</p>
      ) : screen === "inventory" && inventoryDetail ? (
        <CpanelScrollArea
          className="cpanel-stats-detail-shell"
          bodyClassName="cpanel-stats-detail-body"
          resetKey={`${inventoryDetail.userId}-${inventoryDetail.kind}-${inventoryDetail.loading}`}
        >
          {inventoryDetail.error ? (
            <p className="cpanel-view__banner cpanel-view__banner--error">
              {inventoryDetail.error}
            </p>
          ) : null}
          {inventoryDetail.loading ? (
            <p className="cpanel-view__empty">Chargement…</p>
          ) : inventoryDetail.inventory ? (
            <ul className="cpanel-inventory-grid" aria-busy={Boolean(inventoryDetail.busyItemId)}>
              {inventoryItems.map((item) => {
                const counts = inventoryDetail.inventory?.[inventoryDetail.kind] || {};
                const raw = Number(counts[item.id]);
                return (
                  <InventoryItemCard
                    key={item.id}
                    item={item}
                    count={Number.isFinite(raw) && raw > 0 ? raw : 0}
                    busy={inventoryDetail.busyItemId === item.id}
                    onSave={saveInventoryCount}
                  />
                );
              })}
            </ul>
          ) : null}
        </CpanelScrollArea>
      ) : screen === "inventory" ? (
        !loading && inventoryPlayers.length === 0 ? (
          <p className="cpanel-view__empty">Aucun compte à afficher.</p>
        ) : (
          <CpanelScrollArea
            className="cpanel-stats-table-shell"
            bodyClassName="cpanel-stats-table-body"
            resetKey={`${loading ? "skel" : "rows"}-${inventoryPlayers.length}-${selectedInventoryKind.value}`}
            tableHead={
              <div className="cpanel-stats-table-head">
                <table className="cpanel-stats-table cpanel-inventory-table">
                  <InventoryTableColgroup />
                  <thead>
                    <tr>
                      <th scope="col">Pseudo</th>
                      <th scope="col">mail</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                </table>
              </div>
            }
          >
            <table className="cpanel-stats-table cpanel-inventory-table">
              <InventoryTableColgroup />
              <tbody
                className={loading ? "cpanel-stats-table__skel" : undefined}
                aria-busy={loading}
              >
                {loading
                  ? Array.from({ length: STATS_SKELETON_ROWS }, (_, index) => (
                      <tr key={`cpanel-inventory-skel-${index}`}>
                        <td>
                          <CpanelSkel className="cpanel-skel--name" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--email" />
                        </td>
                        <td>
                          <div className="cpanel-inventory-action-cell">
                            <CpanelSkel className="cpanel-skel--action" />
                            <CpanelSkel className="cpanel-skel--eye" />
                          </div>
                        </td>
                      </tr>
                    ))
                  : inventoryPlayers.map((row) => (
                      <tr key={row.userId || row.email || row.displayName}>
                        <td>{row.displayName || "—"}</td>
                        <td>{row.email || "—"}</td>
                        <td>
                          <div className="cpanel-inventory-action-cell">
                            <span className="cpanel-inventory-action-label">
                              {selectedInventoryKind.noun}
                            </span>
                            <button
                              type="button"
                              className="cpanel-stats-eye"
                              onClick={() => openInventoryDetail(row)}
                              disabled={!row.userId || row.hasProfile === false}
                              aria-label={`Modifier les ${selectedInventoryKind.noun} de ${
                                row.displayName || row.email || "ce joueur"
                              }`}
                            >
                              <Eye size={17} aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </CpanelScrollArea>
        )
      ) : screen === "quests" ? (
        !loading && questPlayers.length === 0 ? (
          <p className="cpanel-view__empty">Aucun compte à afficher.</p>
        ) : (
          <CpanelScrollArea
            className="cpanel-stats-table-shell"
            bodyClassName="cpanel-stats-table-body"
            resetKey={`${loading ? "skel" : "rows"}-${questPlayers.length}`}
            tableHead={
              <div className="cpanel-stats-table-head">
                <table className="cpanel-stats-table cpanel-quests-table">
                  <QuestsTableColgroup />
                  <thead>
                    <tr>
                      <th scope="col">Pseudo</th>
                      <th scope="col">mail</th>
                      <th scope="col">
                        <span className="cpanel-quests-col-head">
                          Inst. part.
                          <VocabInfoTip
                            tooltip="Oui : ce joueur peut recevoir une quête donnée par un instructeur particulier. Non : uniquement les instructeurs habituels."
                            ariaLabel="Explication des instructeurs particuliers"
                            placement="below"
                          />
                        </span>
                      </th>
                      <th scope="col">Quête mail</th>
                    </tr>
                  </thead>
                </table>
              </div>
            }
          >
            <table className="cpanel-stats-table cpanel-quests-table">
              <QuestsTableColgroup />
              <tbody
                className={loading ? "cpanel-stats-table__skel" : undefined}
                aria-busy={loading}
              >
                {loading
                  ? Array.from({ length: STATS_SKELETON_ROWS }, (_, index) => (
                      <tr key={`cpanel-quests-skel-${index}`}>
                        <td>
                          <CpanelSkel className="cpanel-skel--name" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--email" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--toggle" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--toggle" />
                        </td>
                      </tr>
                    ))
                  : questPlayers.map((row) => (
                      <tr key={row.userId || row.email || row.displayName}>
                        <td>{row.displayName || "—"}</td>
                        <td>{row.email || "—"}</td>
                        <td>
                          <div className="cpanel-quests-toggle-cell">
                            <CpanelYesNoDropdown
                              value={Boolean(row.specialInstructorsEnabled)}
                              disabled={!row.userId || busyKey === `quest:${row.userId}`}
                              ariaLabel={`Instructeurs particuliers pour ${
                                row.displayName || row.email || "ce joueur"
                              }`}
                              onChange={(enabled) => setPlayerSpecialInstructors(row, enabled)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="cpanel-quests-toggle-cell">
                            <CpanelYesNoDropdown
                              value={row.questEmailEnabled !== false}
                              disabled={!row.userId || busyKey === `quest:${row.userId}`}
                              ariaLabel={`Mail de quête pour ${
                                row.displayName || row.email || "ce joueur"
                              }`}
                              onChange={(enabled) => setPlayerQuestEmail(row, enabled)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </CpanelScrollArea>
        )
      ) : screen === "stats" && statsDetail ? (
        statsDetail.wordDetail ? (
          <CpanelScrollArea
            className="cpanel-stats-detail-shell"
            bodyClassName="cpanel-stats-detail-body"
            resetKey={`${statsDetail.userId}-${statsDetail.wordDetail.mot}-${statsDetail.wordDetail.loading}`}
          >
            {statsDetail.wordDetail.loading ? (
              <p className="cpanel-view__empty">Chargement…</p>
            ) : statsDetail.wordDetail.error ? (
              <p className="cpanel-view__banner cpanel-view__banner--error">
                {statsDetail.wordDetail.error}
              </p>
            ) : (
              <CapturedWordDetailBody data={statsDetail.wordDetail.data} />
            )}
          </CpanelScrollArea>
        ) : statsDetail.questObjectives ? (
          <CpanelScrollArea
            className="cpanel-stats-detail-shell"
            bodyClassName="cpanel-stats-detail-body"
            resetKey={`${statsDetail.userId}-quest-objectives-${statsDetail.questObjectives.date || ""}`}
          >
            <QuestObjectivesDetailBody quest={statsDetail.questObjectives} />
          </CpanelScrollArea>
        ) : statsDetail.metric === "questHistory" ? (
          <CpanelScrollArea
            className="cpanel-stats-table-shell cpanel-quest-history-shell"
            bodyClassName="cpanel-stats-table-body"
            resetKey={`${statsDetail.userId}-questHistory-${statsDetail.loading}`}
            tableHead={<QuestHistoryTableHead />}
          >
            <table className="cpanel-stats-table cpanel-quest-history-table">
              <QuestHistoryTableColgroup />
              <tbody>
                {statsDetail.loading ? (
                  <tr>
                    <td colSpan={8}>
                      <p className="cpanel-view__empty">Chargement…</p>
                    </td>
                  </tr>
                ) : statsDetail.error ? (
                  <tr>
                    <td colSpan={8}>
                      <p className="cpanel-view__banner cpanel-view__banner--error">
                        {statsDetail.error}
                      </p>
                    </td>
                  </tr>
                ) : (
                  <QuestHistoryRows
                    quests={Array.isArray(statsDetail.data?.quests) ? statsDetail.data.quests : []}
                    onOpenObjectives={(quest) =>
                      setStatsDetail((current) =>
                        current ? { ...current, questObjectives: quest } : current
                      )
                    }
                  />
                )}
              </tbody>
            </table>
          </CpanelScrollArea>
        ) : statsDetail.metric === "lastVocabScore" ? (
          <div className="cpanel-stats-detail-shell cpanel-stats-detail-shell--fit">
            <div className="cpanel-stats-detail-body">
              {statsDetail.loading ? (
                <p className="cpanel-view__empty">Chargement…</p>
              ) : statsDetail.error ? (
                <p className="cpanel-view__banner cpanel-view__banner--error">{statsDetail.error}</p>
              ) : (
                <StatDetailBody metric={statsDetail.metric} data={statsDetail.data} />
              )}
            </div>
          </div>
        ) : (
          <CpanelScrollArea
            className="cpanel-stats-detail-shell"
            bodyClassName="cpanel-stats-detail-body"
            resetKey={`${statsDetail.userId}-${statsDetail.metric}-${statsDetail.loading}`}
          >
            {statsDetail.loading ? (
              <p className="cpanel-view__empty">Chargement…</p>
            ) : statsDetail.error ? (
              <p className="cpanel-view__banner cpanel-view__banner--error">{statsDetail.error}</p>
            ) : (
              <StatDetailBody
                metric={statsDetail.metric}
                data={statsDetail.data}
                onOpenCapturedWord={openCapturedWord}
              />
            )}
          </CpanelScrollArea>
        )
      ) : screen === "stats" ? (
        !loading && stats.length === 0 ? (
          <p className="cpanel-view__empty">Aucun compte à afficher.</p>
        ) : (
          <CpanelScrollArea
            className="cpanel-stats-table-shell"
            bodyClassName="cpanel-stats-table-body"
            resetKey={`${loading ? "skel" : "rows"}-${stats.length}-${selectedStatMetric.value}`}
            tableHead={
              <div className="cpanel-stats-table-head">
                <table className="cpanel-stats-table">
                  <StatsTableColgroup />
                  <thead>
                    <tr>
                      <th scope="col">Pseudo</th>
                      <th scope="col">mail</th>
                      <th scope="col">LLI</th>
                      <th scope="col">LLO</th>
                      <th scope="col">
                        {selectedStatMetric.label.replace(/\bnombre\b/gi, "nbr")}
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>
            }
          >
            <table className="cpanel-stats-table">
              <StatsTableColgroup />
              <tbody
                className={loading ? "cpanel-stats-table__skel" : undefined}
                aria-busy={loading}
              >
                {loading
                  ? Array.from({ length: STATS_SKELETON_ROWS }, (_, index) => (
                      <tr key={`cpanel-stats-skel-${index}`}>
                        <td>
                          <CpanelSkel className="cpanel-skel--name" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--email" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--when" />
                        </td>
                        <td>
                          <CpanelSkel className="cpanel-skel--when" />
                        </td>
                        <td>
                          <div className="cpanel-stats-metric-cell">
                            <CpanelSkel className="cpanel-skel--metric" />
                            {selectedStatMetric.hasDetail ? (
                              <CpanelSkel className="cpanel-skel--eye" />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  : stats.map((row) => {
                      const eyeDisabled = isStatEyeDisabled(
                        row,
                        selectedStatMetric.value
                      );
                      return (
                        <tr key={row.userId || row.email || row.displayName}>
                          <td>{row.displayName || "—"}</td>
                          <td>{row.email || "—"}</td>
                          <td>{formatAdminWhen(row.lastLoginAt)}</td>
                          <td>{formatAdminWhen(row.lastLogoutAt)}</td>
                          <td>
                            <div className="cpanel-stats-metric-cell">
                              <span className="cpanel-stats-metric-text">
                                {formatAdminMetricValue(
                                  row,
                                  selectedStatMetric.value
                                )}
                              </span>
                              {selectedStatMetric.hasDetail ? (
                                <button
                                  type="button"
                                  className="cpanel-stats-eye"
                                  onClick={() => openStatDetail(row)}
                                  disabled={eyeDisabled}
                                  aria-label="Voir le détail"
                                >
                                  <Eye size={17} aria-hidden="true" />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </CpanelScrollArea>
        )
      ) : screen === "accounts" ? (
        filteredAccounts.length === 0 ? (
          <p className="cpanel-view__empty">Aucun compte à afficher.</p>
        ) : (
          <CpanelScrollArea
            bodyClassName="cpanel-view__list-body"
            resetKey={filteredAccounts.map((item) => item.userId || item.email).join("|")}
          >
          <ul className="cpanel-view__list">
            {filteredAccounts.map((account) => {
              const self = isOwnAccount(account, user);
              const locked = account.isAdmin || self || !account.userId;
              return (
                <li key={account.userId || account.email || account.username} className="cpanel-card">
                  <div className="cpanel-card__main">
                    <p className="cpanel-card__title">
                      {account.email || account.displayName || account.userId || "Compte"}
                    </p>
                    <p className="cpanel-card__meta">
                      {account.displayName ? `${account.displayName} · ` : ""}
                      {ACCOUNT_STATUS_LABELS[account.status] || account.status || "Compte"}
                      {account.orphan ? " · orphelin Dynamo" : ""}
                      {account.isAdmin ? " · administrateur" : ""}
                      {` · ${formatWhen(account.createdAt)}`}
                    </p>
                  </div>
                  <div className="cpanel-card__actions">
                    {!account.orphan && account.userId ? (
                      <button
                        type="button"
                        className="word-edit-btn word-edit-btn--cancel"
                        onClick={() => openMfaAccount(account)}
                      >
                        MFA
                      </button>
                    ) : null}
                    {locked ? (
                      <p className="cpanel-card__locked">
                        {account.isAdmin
                          ? "Compte administrateur"
                          : self
                            ? "Votre compte"
                            : "Non supprimable"}
                      </p>
                    ) : (
                      <button
                        type="button"
                        className="word-edit-btn word-edit-btn--danger"
                        onClick={() => {
                          setPendingDelete(account);
                          setTypedConfirm("");
                          setDeleteError("");
                          setInfo("");
                        }}
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          </CpanelScrollArea>
        )
      ) : filteredRequests.length === 0 ? (
        <p className="cpanel-view__empty">Aucune demande à afficher.</p>
      ) : (
        <CpanelScrollArea
          bodyClassName="cpanel-view__list-body"
          resetKey={filteredRequests.map((item) => item.email).join("|")}
        >
        <ul className="cpanel-view__list">
          {filteredRequests.map((request) => {
            const deciding = busyKey === `approve:${request.email}` || busyKey === `reject:${request.email}`;
            const removing = busyKey === `remove:${request.email}`;
            const canDecide = request.status === "pending" || request.status === "approved" || request.status === "rejected";
            const canApprove = request.status !== "completed";
            const canReject = request.status === "pending" || request.status === "approved";
            return (
              <li key={request.email} className="cpanel-card">
                <div className="cpanel-card__main">
                  <p className="cpanel-card__title">{request.email}</p>
                  <p className="cpanel-card__meta">
                    {REQUEST_STATUS_LABELS[request.status] || request.status}
                    {` · ${formatWhen(request.createdAt)}`}
                    {request.status === "approved" && request.inviteExpiresAt
                      ? ` · invitation jusqu'au ${formatWhen(request.inviteExpiresAt)}`
                      : ""}
                  </p>
                </div>
                <div className="cpanel-card__actions">
                  {canApprove && canDecide ? (
                    <button
                      type="button"
                      className="word-edit-btn word-edit-btn--primary"
                      disabled={Boolean(busyKey)}
                      onClick={() => decideRequest(request.email, "approve")}
                    >
                      {deciding && busyKey.startsWith("approve:")
                        ? "Envoi…"
                        : request.status === "approved"
                          ? "Renvoyer"
                          : "Accepter"}
                    </button>
                  ) : null}
                  {canReject ? (
                    <button
                      type="button"
                      className="word-edit-btn word-edit-btn--danger"
                      disabled={Boolean(busyKey)}
                      onClick={() => decideRequest(request.email, "reject")}
                    >
                      {deciding && busyKey.startsWith("reject:") ? "Refus…" : "Refuser"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="word-edit-btn word-edit-btn--cancel"
                    disabled={Boolean(busyKey)}
                    onClick={() => removeRequest(request.email)}
                  >
                    {removing ? "Retrait…" : "Retirer"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        </CpanelScrollArea>
      )}

      {pendingDelete
        ? createPortal(
            <div
              className="delete-word-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeDelete();
              }}
            >
              <div
                className="delete-word-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cpanel-delete-title"
              >
                <button
                  type="button"
                  className="delete-word-modal__close"
                  onClick={closeDelete}
                  disabled={deleting}
                  aria-label="Fermer"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
                <h3 id="cpanel-delete-title" className="delete-word-modal__title">
                  Supprimer ce compte ?
                </h3>
                <p className="delete-word-modal__text">
                  Cognito, le profil Dynamo et les quêtes de ce joueur seront effacés.
                  Pour confirmer, tapez{" "}
                  <strong className="delete-word-modal__word">{expectedConfirm}</strong>.
                </p>
                <input
                  type="text"
                  className="delete-word-modal__input"
                  value={typedConfirm}
                  onChange={(event) => setTypedConfirm(event.target.value)}
                  placeholder="Tapez l'e-mail ou l'identifiant…"
                  autoFocus
                  disabled={deleting}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && confirmMatches && !deleting) {
                      confirmDeleteAccount();
                    }
                  }}
                />
                {deleteError ? <p className="delete-word-modal__error">{deleteError}</p> : null}
                <div className="delete-word-modal__actions">
                  <button
                    type="button"
                    className="delete-word-modal__btn delete-word-modal__btn--cancel"
                    onClick={closeDelete}
                    disabled={deleting}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="delete-word-modal__btn delete-word-modal__btn--confirm"
                    onClick={confirmDeleteAccount}
                    disabled={!confirmMatches || deleting}
                  >
                    {deleting ? "Suppression…" : "Valider"}
                  </button>
                </div>
              </div>
            </div>,
            getAppPortalRoot()
          )
        : null}

      {mfaAccount
        ? createPortal(
            <div
              className="delete-word-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeMfa();
              }}
            >
              <div
                className="delete-word-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cpanel-mfa-title"
              >
                <button
                  type="button"
                  className="delete-word-modal__close"
                  onClick={closeMfa}
                  disabled={mfaBusy}
                  aria-label="Fermer"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
                <h3 id="cpanel-mfa-title" className="delete-word-modal__title">
                  Authentification renforcée
                </h3>
                <p className="delete-word-modal__text">
                  {mfaAccount.email || mfaAccount.displayName || "Ce compte"}
                  {mfaLoading
                    ? " — lecture…"
                    : mfaEnabled
                      ? " — active. Seul un téléphone perdu justifie de la couper ici : le joueur devra la réactiver lui-même."
                      : mfaEnabled === false
                        ? " — inactive. Seul le joueur peut l'activer, depuis son profil."
                        : ""}
                </p>
                {mfaError ? <p className="delete-word-modal__error">{mfaError}</p> : null}
                <div className="delete-word-modal__actions">
                  <button
                    type="button"
                    className="delete-word-modal__btn delete-word-modal__btn--cancel"
                    onClick={closeMfa}
                    disabled={mfaBusy}
                  >
                    Fermer
                  </button>
                  {mfaEnabled ? (
                    <button
                      type="button"
                      className="delete-word-modal__btn delete-word-modal__btn--confirm"
                      onClick={disableMfaAccount}
                      disabled={mfaBusy}
                    >
                      {mfaBusy ? "Désactivation…" : "Désactiver"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>,
            getAppPortalRoot()
          )
        : null}
    </div>
  );
}
